import { CCPPricing, Currency, PricingPlanType } from '@atlassian/cssxp-typescommon';
import { isStatusPageAddOnProduct } from '@css-xp/ppc-utils';
import { STATUSPAGE_PRODUCT_ID } from '@css-xp/ppc-constants';
import { getSelectedAppsData, stitchChangeKeyObjects } from '../utils';
import type { State } from '../types';
import { getPricingPlanId } from '../utils/get-pricing-plan-id';
import { CartLinkedEntitlementsSchema, CartProductSchema } from '../../types';

function getPricingPlanType(
  productOrLinkedEntitlement: CartProductSchema | CartLinkedEntitlementsSchema,
  state: State
): PricingPlanType {
  const initialStoreProducts = state.products;
  const { productId: productKey, offeringId } = productOrLinkedEntitlement;
  const selectedBillingProfileDetails = state.billingProfiles?.find(
    (billingProfile) => billingProfile.id === state.invoiceGroupId
  );

  /**
   * we are converting productId = offeringId for statuspage addons and saving in store.
   * we are making this change because all statuspage addons have same product id
   * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
   */
  const productId = isStatusPageAddOnProduct(productKey) ? STATUSPAGE_PRODUCT_ID : productKey;
  const currentPricingEligibility = (selectedBillingProfileDetails?.shipToPriceEligibility?.toUpperCase() ??
    CCPPricing.COMMERCIAL) as CCPPricing;
  const currentCurrency = (selectedBillingProfileDetails?.currency?.toUpperCase() ?? Currency.USD) as Currency;

  // Get the pricing plan id based on the pricing eligibility and currency, If not supported, then get the commercial pricing plan id
  const pricingPlanId = getPricingPlanId({
    offerings: state.offeringsAndpricingPlans?.[productId],
    offeringId: offeringId,
    currency: currentCurrency,
    pricingEligibility: currentPricingEligibility,
    defaultPricingEligibilityToCommercial: true,
    enableRetainPricingPlan: true,
    initialPricingPlanId: productOrLinkedEntitlement.pricingPlanId,
    initialOfferingId: initialStoreProducts[productId]?.offeringId,
    pricingPlanType: productOrLinkedEntitlement.pricingPlanType,
    entitlementPriceEligibility: productOrLinkedEntitlement.priceEligibility,
  });

  // Get the price eligibility from the pricing plan id
  const selectedOfferingDetails = state.offeringsAndpricingPlans?.[productId]?.find(({ id }) => id === offeringId);
  const pricingPlanTypeBasedOnPricingPlanId = selectedOfferingDetails?.pricingPlans?.find((p) => p.id === pricingPlanId)
    ?.type as PricingPlanType;

  return pricingPlanTypeBasedOnPricingPlanId;
}

export function getPricingPlanEligibility(state: State): Record<string, PricingPlanType> {
  if (!state.offeringsAndpricingPlans) return {};
  const selectedProducts = Object.values(stitchChangeKeyObjects(state.products, state.selectedProducts));
  const selectedLinkedEntitlements = getSelectedAppsData(state);

  const productIdPricingEligibilityMap: Record<string, PricingPlanType> = {};

  selectedProducts.forEach((product) => {
    productIdPricingEligibilityMap[product.productId] = getPricingPlanType(product, state);
  });

  selectedLinkedEntitlements?.forEach((app) => {
    productIdPricingEligibilityMap[app.productId] = getPricingPlanType(app, state);
  });

  return productIdPricingEligibilityMap;
}
